/**
 * Tiny helpers around Supabase responses.
 *
 * Supabase returns `{ data, error }` tuples; React Query expects
 * thrown errors. Repeating the boilerplate at every call site adds
 * noise *and* hides bugs (it is easy to forget the `if (error) throw`).
 *
 * These helpers eliminate that boilerplate while preserving full
 * type inference, so call sites read like:
 *
 *   const patients = await unwrap(
 *     supabase.from('patients').select('*').eq('organization_id', orgId),
 *   );
 */

import type { PostgrestError, PostgrestSingleResponse } from '@supabase/supabase-js';

/**
 * Helper for typing a row that has been augmented with a PostgREST
 * relation join. PostgREST's `select('*, patient:patients(...)')`
 * returns the base row plus a nested object — but the supabase-js
 * generated types do not infer that from a literal `select()` string.
 *
 * Use it like:
 *
 *   import type { Tables } from '@/integrations/supabase/types';
 *   type WaitlistRow = WithRelation<
 *     Tables<'waitlist_entries'>,
 *     'patient',
 *     Pick<Tables<'patients'>, 'first_name' | 'last_name'> | null
 *   >;
 *
 * The relation type is `RT` directly (not `RT[]`) so callers can pass
 * `null` or pick the array form themselves — PostgREST returns a
 * single object for to-one joins and an array for to-many joins.
 */
export type WithRelation<T, K extends string, RT> = T & { [P in K]: RT };

/**
 * Throws if the Supabase response carries an error, otherwise returns
 * `data`. Generic so the inferred type matches the caller's `.select()`.
 *
 * Awaitable: pass the query builder directly.
 */
export async function unwrap<T>(
  query: PromiseLike<PostgrestSingleResponse<T>>,
): Promise<T> {
  const { data, error } = await query;
  if (error) throw toSupabaseError(error);
  // Supabase returns `null` when nothing matches a `.maybeSingle()` —
  // callers expecting that should use `unwrapMaybe` instead.
  if (data == null) {
    throw new Error('Supabase query returned no data');
  }
  return data;
}

/**
 * Like `unwrap` but allows `null` (for `.maybeSingle()` queries).
 */
export async function unwrapMaybe<T>(
  query: PromiseLike<PostgrestSingleResponse<T | null>>,
): Promise<T | null> {
  const { data, error } = await query;
  if (error) throw toSupabaseError(error);
  return data ?? null;
}

interface AnnotatedError extends Error {
  code?: string;
  details?: string;
  hint?: string;
}

/**
 * Coerce a {@link PostgrestError} into a regular Error so it survives
 * Sentry, React Query and `instanceof Error` checks.
 *
 * The original metadata (code/details/hint) is attached to the new
 * Error instance for debugging — without leaking patient data, since
 * Supabase never echoes row content into these fields.
 */
export function toSupabaseError(err: PostgrestError): Error {
  const e: AnnotatedError = new Error(err.message || 'Supabase error');
  e.code = err.code;
  e.details = err.details;
  e.hint = err.hint;
  return e;
}

/**
 * Pull a human-readable error message out of a Supabase Functions
 * error.
 *
 * The Supabase SDK throws a few different error shapes from
 * `functions.invoke()`:
 *
 *  - `FunctionsFetchError` — network / CORS / 404. Message is the
 *    generic "Failed to send a request to the Edge Function".
 *  - `FunctionsHttpError` — non-2xx response. Has `.context` which is
 *    the underlying `Response` object, so we can read the JSON body
 *    the edge function returned (e.g. `{ error: "..." }`).
 *  - `FunctionsRelayError` — gateway-level failure.
 *
 * For all of them we prefer the edge function's own `error` field when
 * it exists, falling back to `err.message`. Safe to call with `null` or
 * non-Error values.
 */
export async function parseFunctionsError(err: unknown): Promise<string> {
  if (!err) return 'Unbekannter Fehler';
  const anyErr = err as {
    message?: string;
    name?: string;
    context?: Response | { json?: () => Promise<unknown>; text?: () => Promise<string> };
  };
  const fallback = anyErr?.message || 'Unbekannter Fehler';

  const ctx = anyErr?.context;
  if (!ctx) return fallback;

  // `context` on FunctionsHttpError is a `Response`. It may only be
  // consumed once, so clone it if possible.
  try {
    const response = typeof (ctx as Response).clone === 'function'
      ? (ctx as Response).clone()
      : (ctx as Response);

    if (typeof (response as Response).json === 'function') {
      const body = (await (response as Response).json()) as
        | { error?: string; message?: string }
        | string
        | null;
      if (typeof body === 'string' && body) return body;
      if (body && typeof body === 'object') {
        if (body.error) return body.error;
        if (body.message) return body.message;
      }
    } else if (typeof (response as { text?: () => Promise<string> }).text === 'function') {
      const text = await (response as { text: () => Promise<string> }).text();
      if (text) return text;
    }
  } catch {
    // Fall through to the default message if the body can't be parsed.
  }
  return fallback;
}
